import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError, exportCsv } from "@/lib/api";
import { inr, dateFmt } from "@/lib/format";
import { PageLoader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wallet, Check, X, Plus, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Search } from "lucide-react";

export default function Redemptions() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-redemptions"],
    queryFn: async () => (await api.get("/admin/redemptions")).data,
  });
  const { data: authors } = useQuery({
    queryKey: ["authors"],
    queryFn: async () => (await api.get("/admin/authors")).data,
  });

  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ author_user_id: "", amount: "", status: "approved", note: "", transaction_id: "", date: "" });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [approveFor, setApproveFor] = useState(null);
  const [approveTxn, setApproveTxn] = useState("");
  const [approveDate, setApproveDate] = useState("");
  const [search, setSearch] = useState("");

  const act = useMutation({
    mutationFn: async ({ id, status, transaction_id, date }) => (await api.put(`/admin/redemptions/${id}`, { status, transaction_id, date })).data,
    onSuccess: () => {
      toast.success("Redemption updated");
      setApproveFor(null);
      setApproveTxn("");
      setApproveDate("");
      qc.invalidateQueries({ queryKey: ["admin-redemptions"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const createManual = useMutation({
    mutationFn: async () => (await api.post("/admin/redemptions", {
      author_user_id: manual.author_user_id, amount: parseFloat(manual.amount), status: manual.status,
      note: manual.note, transaction_id: manual.transaction_id || null, date: manual.date || null,
    })).data,
    onSuccess: () => {
      toast.success("Manual redemption recorded");
      setManualOpen(false);
      setManual({ author_user_id: "", amount: "", status: "approved", note: "", transaction_id: "", date: "" });
      qc.invalidateQueries({ queryKey: ["admin-redemptions"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const bulkUpload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post("/admin/redemptions/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      setBulkResult(d);
      toast.success(`${d.processed} of ${d.total_rows} redemptions recorded`);
      qc.invalidateQueries({ queryKey: ["admin-redemptions"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/admin/redemptions/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "redemptions_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  const payoutText = (r) => {
    if (r.payout_method === "upi") return r.upi_id ? `UPI · ${r.upi_id}` : "UPI";
    if (r.payout_method === "bank") return [r.account_name, r.account_number, r.ifsc].filter(Boolean).join(" · ") || "Bank";
    if (r.manual) return "Manual entry";
    return "—";
  };

  const manualValid = manual.author_user_id && parseFloat(manual.amount) > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Redemption requests</p>
          <p className="text-sm text-muted-foreground mt-1">Approve or reject author payout requests. Pending & approved amounts reduce available balance.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" data-testid="export-redemptions" onClick={() => exportCsv("/admin/redemptions/export", "redemptions.csv").catch(() => toast.error("Export failed"))}>
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="outline" className="gap-2" data-testid="bulk-redemption-button" onClick={() => { setBulkResult(null); setBulkOpen(true); }}>
            <Upload className="h-4 w-4" /> Bulk Import
          </Button>
          <Button className="gap-2" data-testid="manual-redemption-button" onClick={() => setManualOpen(true)}>
            <Plus className="h-4 w-4" /> Manual Redemption
          </Button>
        </div>
      </div>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Record a redemption</DialogTitle>
            <DialogDescription>Manually log a payout for an author (e.g. an offline payment).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Author</Label>
              <Select value={manual.author_user_id} onValueChange={(v) => setManual({ ...manual, author_user_id: v })}>
                <SelectTrigger data-testid="manual-author-select"><SelectValue placeholder="Select author" /></SelectTrigger>
                <SelectContent>
                  {authors?.map((a) => <SelectItem key={a.user_id} value={a.user_id}>{a.name} (@{a.user_id})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹)</Label>
                <Input data-testid="manual-amount" type="number" min="0" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} placeholder="5000" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={manual.status} onValueChange={(v) => setManual({ ...manual, status: v })}>
                  <SelectTrigger data-testid="manual-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Redemption date (optional)</Label>
              <Input data-testid="manual-date" type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Transaction ID (optional)</Label>
              <Input data-testid="manual-txn" value={manual.transaction_id} onChange={(e) => setManual({ ...manual, transaction_id: e.target.value })} placeholder="e.g. TXN123456" />
            </div>
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea data-testid="manual-note" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} placeholder="Reference / remarks" className="min-h-[70px]" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-manual-redemption" onClick={() => createManual.mutate()} disabled={!manualValid || createManual.isPending}>
              {createManual.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record redemption"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head">Bulk import redemptions</DialogTitle>
            <DialogDescription>Upload a CSV/XLSX with columns: user_id, amount (optional: status, note).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Button variant="outline" className="gap-2" data-testid="download-redemptions-template" onClick={downloadTemplate}>
              <Download className="h-4 w-4" /> Download template
            </Button>
            <label htmlFor="bulk-redemption-file" className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl py-10 cursor-pointer hover:bg-muted/40 transition-colors">
              {bulkUpload.isPending ? <Loader2 className="h-7 w-7 text-primary animate-spin" /> : <FileSpreadsheet className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />}
              <p className="text-sm font-medium">Choose a .csv or .xlsx file</p>
              <input
                id="bulk-redemption-file" type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="bulk-redemption-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setBulkResult(null); bulkUpload.mutate(f); } e.target.value = ""; }}
              />
            </label>
            {bulkResult && (
              <div data-testid="bulk-redemption-result" className="rounded-lg border border-border p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> <span className="font-medium">{bulkResult.processed}</span> of {bulkResult.total_rows} recorded.</div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="text-xs text-muted-foreground list-disc pl-6 max-h-40 overflow-y-auto">
                    {bulkResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!approveFor} onOpenChange={(o) => !o && setApproveFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head">Approve payout</DialogTitle>
            <DialogDescription>Record the transaction reference for {approveFor?.author_name}'s payout of {approveFor ? inr(approveFor.amount) : ""}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Transaction ID (optional)</Label>
              <Input data-testid="approve-txn" value={approveTxn} onChange={(e) => setApproveTxn(e.target.value)} placeholder="e.g. TXN123456" />
            </div>
            <div className="space-y-2">
              <Label>Payment date (optional)</Label>
              <Input data-testid="approve-date" type="date" value={approveDate} onChange={(e) => setApproveDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-approve" onClick={() => act.mutate({ id: approveFor.id, status: "approved", transaction_id: approveTxn || null, date: approveDate || null })} disabled={act.isPending}>
              {act.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Approve payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="overflow-hidden">
        {isLoading ? (
          <PageLoader />
        ) : data.length === 0 ? (
          <EmptyState icon={Wallet} title="No redemption requests" desc="Author payout requests will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <div className="px-4 py-3 border-b border-border">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="redemption-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by author name or ID…" className="pl-9" />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Author</TableHead>
                  <TableHead>Payout details</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.filter((r) => {
                  const q = search.trim().toLowerCase();
                  if (!q) return true;
                  return (r.author_name || "").toLowerCase().includes(q) || (r.author_user_id || "").toLowerCase().includes(q);
                }).map((r) => (
                  <TableRow key={r.id} data-testid={`redemption-row-${r.id}`}>
                    <TableCell className="font-medium">{r.author_name}<span className="text-muted-foreground font-normal"> @{r.author_user_id}</span></TableCell>
                    <TableCell className="text-muted-foreground max-w-xs">
                      <span className="block truncate">{payoutText(r)}</span>
                      {r.transaction_id && <span className="block text-xs truncate font-mono text-foreground/80" data-testid={`txn-${r.id}`}>Txn: {r.transaction_id}</span>}
                      {r.note && <span className="block text-xs truncate opacity-80">{r.note}</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{dateFmt(r.created_at)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{inr(r.amount)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-right">
                      {r.status === "pending" ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="gap-1 text-emerald-700 hover:text-emerald-700 dark:text-emerald-400" data-testid={`approve-${r.id}`} onClick={() => { setApproveFor(r); setApproveTxn(""); setApproveDate(""); }}>
                            <Check className="h-3.5 w-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="gap-1 text-destructive hover:text-destructive" data-testid={`reject-${r.id}`} onClick={() => act.mutate({ id: r.id, status: "rejected" })}>
                            <X className="h-3.5 w-3.5" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">{dateFmt(r.processed_at)}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
